'use client'

import textActionM from '@fukku/styles/texts/textActionM.module.css'
import textBodyS from '@fukku/styles/texts/textBodyS.module.css'
import textLabelS from '@fukku/styles/texts/textLabelS.module.css'
import { sendGenericEvent } from 'analytics/events/generics/sendGenericEvent'
import { cx } from 'classix'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { GenericEvents } from 'product/types/GenericEvents'
import type { ProductSize } from 'product/types/Product'
import type { ProductLocations } from 'product/types/ProductLocations'
import type { StockAvailability } from 'product/types/Stock'
import { useCallback } from 'react'
import type { DelayedSize } from '../../hooks/useProductSelectedColorSizes/useProductSelectedColorSizes'
import type { SelectedSize } from '../SizesList'
import styles from './Size.module.css'

const getSizeDataTestId = (size: string, available: boolean) =>
	`product.sizeSelector.${available ? 'available' : 'unavailable'}.${size}`

export function Size({
	size,
	selectedColorId,
	selectedProductId,
	delayedMessage,
	location,
	onSelectedSize,
}: {
	readonly selectedColorId: string
	readonly selectedProductId: string
	readonly size: ProductSize & StockAvailability & DelayedSize
	readonly location?: ProductLocations
	readonly delayedMessage?: string
	readonly onSelectedSize: (selectedSize: SelectedSize) => void
}) {
	const { t } = useLabels()
	const shouldRenderRightSide =
		size.lastUnits || size.isDelayed || !size.available

	const sizeId = getSizeDataTestId(size.id, size.available)

	const clickHandler = useCallback(() => {
		sendGenericEvent(GenericEvents.CLICK_SIZE, {
			last_units: !!size.lastUnits,
			location,
		})
		onSelectedSize({
			size,
			color: selectedColorId,
		})
	}, [
		onSelectedSize,
		selectedProductId,
		selectedColorId,
		size.available,
		size.id,
		size.relatedProductId,
		size.lastUnits,
		location,
	])

	return (
		<button
			type='button'
			className={styles.size}
			aria-label={size.label}
			onClick={clickHandler}
			data-testid={sizeId}
			id={sizeId}
			aria-expanded={size.available ? true : undefined}
			aria-haspopup={!size.available ? 'dialog' : undefined}
		>
			<div
				className={cx(!size.available && styles.unavailable, styles.sizeInfo)}
			>
				<span className={textActionM.className}>{size.label}</span>
				{size.extraInfo && (
					<span className={`${styles.extraInfo} ${textBodyS.className}`}>
						{size.extraInfo}
					</span>
				)}
			</div>

			{shouldRenderRightSide && (
				<div className={styles.sizeExtraInfo}>
					{!size.available && (
						<span className={textBodyS.className}>
							{t('product.sizeSelector.notifyMe.link')}
						</span>
					)}
					{size.lastUnits && (
						<span className={`${textLabelS.className} ${styles.lastUnits}`}>
							{t('productCard.lastItems.text')}
						</span>
					)}
					{size.isDelayed && (
						<span className={styles.delayedMessage}>{delayedMessage}</span>
					)}
				</div>
			)}
		</button>
	)
}
